import type { SettingLevel, SettingOriginName, SettingsSpec, SettingValue } from "@/api";
import { groupSpec, humanize } from "@/lib/settings";
import { Button, SettingRow } from "@/ui";
import { SettingControl, type ChoiceOption } from "./SettingControl";

export interface SettingRowModel {
  value: SettingValue | undefined;
  /** Uniform origin; undefined (rendered as "mixed") when a bulk selection disagrees. */
  origin?: SettingOriginName;
  /** Bulk: the selection disagrees on the value. */
  mixed?: boolean;
  /** Bulk: some (not all) rows override this key — offer a separate reset. */
  partialOverride?: number;
  editable: boolean;
  /** Shown under the help when the key cannot be edited at this level. */
  note?: string;
}

interface SettingsFormProps {
  spec: SettingsSpec;
  level: SettingLevel;
  row: (key: string) => SettingRowModel;
  onSet: (key: string, value: SettingValue) => void;
  onReset: (key: string) => void;
  /** Keys to show (default: all spec keys). */
  include?: (key: string) => boolean;
  /** Picker options for str keys (e.g. alert_channel → target channels). */
  optionsFor?: (key: string) => ChoiceOption[] | undefined;
  /** Text for what a reset returns to, per key. */
  inheritedLabel?: (key: string) => string | undefined;
  disabled?: boolean;
  /** True while a save for this key is in flight (inline spinner). */
  saving?: (key: string) => boolean;
}

/** Settings grouped by spec group, one SettingRow (with origin badge + reset) per key. */
export function SettingsForm({
  spec,
  level,
  row,
  onSet,
  onReset,
  include = () => true,
  optionsFor,
  inheritedLabel,
  disabled,
  saving,
}: SettingsFormProps) {
  const groups = groupSpec(spec, (k) => include(k));
  return (
    <div>
      {groups.map((g) => (
        <section
          key={g.group}
          className="settings-group"
          aria-labelledby={groups.length > 1 ? `sg-${g.group}` : undefined}
          aria-label={groups.length > 1 ? undefined : g.label}
        >
          {groups.length > 1 ? (
            <h3 id={`sg-${g.group}`} className="settings-group__title">
              {g.label}
            </h3>
          ) : null}
          {g.keys.map((key) => {
            const s = spec[key];
            const m = row(key);
            const back = inheritedLabel?.(key);
            const help = [s.help, m.note].filter(Boolean).join(" · ") || undefined;
            return (
              <SettingRow
                key={key}
                settingKey={key}
                label={humanize(key)}
                help={help}
                origin={m.origin ?? "mixed"}
                level={level}
                disabled={!m.editable}
                saving={saving?.(key)}
                onReset={m.editable ? () => onReset(key) : undefined}
                inheritedValueLabel={back}
                resetLabel={`Reset ${humanize(key)} to inherited`}
                control={(ids) => (
                  <span className="row">
                    <SettingControl
                      settingKey={key}
                      spec={s}
                      value={m.value}
                      mixed={m.mixed}
                      onCommit={(v) => onSet(key, v)}
                      disabled={disabled || !m.editable}
                      labelledBy={ids.labelId}
                      describedBy={ids.helpId}
                      options={optionsFor?.(key)}
                    />
                    {m.partialOverride && m.editable ? (
                      <Button size="sm" variant="ghost" onClick={() => onReset(key)}>
                        Reset {m.partialOverride} overridden
                      </Button>
                    ) : null}
                  </span>
                )}
              />
            );
          })}
        </section>
      ))}
    </div>
  );
}

